import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { eventsService, State, District } from '../../services/eventsService';
import { MapPin, ChevronDown, ChevronRight, X } from 'lucide-react';
import { LoadingSpinner } from './LoadingSpinner';

interface StatesDistrictsModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export const StatesDistrictsModal: React.FC<StatesDistrictsModalProps> = ({ open, onOpenChange }) => {
    const [states, setStates] = useState<State[]>([]);
    const [districtsByState, setDistrictsByState] = useState<Record<number, District[]>>({});
    const [expandedStates, setExpandedStates] = useState<Set<number>>(new Set());
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (open) {
            fetchStatesAndDistricts();
        }
    }, [open]);

    const fetchStatesAndDistricts = async () => {
        setLoading(true);
        setError(null);
        try {
            const statesData = await eventsService.getStates();
            setStates(statesData);

            // Fetch districts for all states
            const districtsPromises = statesData.map((state) =>
                eventsService.getDistrictsByState(state.id).then((districts) => ({
                    stateId: state.id,
                    districts,
                }))
            );

            const districtsResults = await Promise.all(districtsPromises);
            const districtsMap: Record<number, District[]> = {};
            districtsResults.forEach(({ stateId, districts }) => {
                districtsMap[stateId] = districts;
            });
            setDistrictsByState(districtsMap);

            // Expand all states by default
            setExpandedStates(new Set(statesData.map((s) => s.id)));
        } catch (err) {
            console.error('Error fetching states and districts:', err);
            setError('Failed to load states and districts. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const toggleState = (stateId: number) => {
        const newExpanded = new Set(expandedStates);
        if (newExpanded.has(stateId)) {
            newExpanded.delete(stateId);
        } else {
            newExpanded.add(stateId);
        }
        setExpandedStates(newExpanded);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="w-[95vw] sm:w-[90vw] md:w-[85vw] lg:w-[80vw] xl:max-w-3xl max-h-[90vh] sm:max-h-[85vh] overflow-hidden !flex !flex-col !gap-0">
                <button
                    onClick={() => onOpenChange(false)}
                    className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-white transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-gray-950 focus:ring-offset-2 disabled:pointer-events-none z-10"
                >
                    <X className="h-4 w-4" />
                    <span className="sr-only">Close</span>
                </button>
                <DialogHeader className="pr-8">
                    <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
                        <MapPin className="w-4 h-4 sm:w-5 sm:h-5" />
                        States and Districts
                    </DialogTitle>
                    <DialogDescription className="text-xs sm:text-sm">View all states and their districts where face authentication–based attendance has been enabled.</DialogDescription>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto mt-4 -mx-6 px-6">
                    {loading ? (
                        <div className="flex justify-center items-center py-12">
                            <LoadingSpinner />
                        </div>
                    ) : error ? (
                        <div className="text-center py-12 text-red-600 text-sm sm:text-base">{error}</div>
                    ) : states.length === 0 ? (
                        <div className="text-center py-12 text-gray-500 text-sm sm:text-base">No states found.</div>
                    ) : (
                        <div className="space-y-2 pb-2">
                            {states.map((state) => {
                                const districts = districtsByState[state.id] || [];
                                const isExpanded = expandedStates.has(state.id);

                                return (
                                    <div key={state.id} className="border border-gray-200 rounded-lg overflow-hidden">
                                        <button onClick={() => toggleState(state.id)} className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-gray-50 hover:bg-gray-100 transition-colors flex items-center justify-between text-left gap-2">
                                            <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                                                {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-gray-500 flex-shrink-0" />}
                                                <span className="font-semibold text-gray-900 text-sm sm:text-base truncate">{state.name}</span>
                                            </div>
                                            <span className="text-xs sm:text-sm text-gray-600 whitespace-nowrap flex-shrink-0">
                                                {districts.length} {districts.length === 1 ? 'district' : 'districts'}
                                            </span>
                                        </button>
                                        {isExpanded && (
                                            <div className="px-3 sm:px-4 py-2 sm:py-3 bg-white border-t border-gray-200">
                                                {districts.length === 0 ? (
                                                    <div className="text-xs sm:text-sm text-gray-500 italic">No districts available</div>
                                                ) : (
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                                                        {districts.map((district) => (
                                                            <div key={district.id} className="text-xs sm:text-sm text-gray-700 py-1 px-2 bg-gray-50 rounded">
                                                                {district.name}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};
